(ns bp-clients.tv-app.nav.views.authenticated
  (:require
   ["react" :refer [createRef]]
   [bp-clients.tv-app.auth.subs :as auth-subs]
   [bp-clients.tv-app.common.components.player :refer [player]]
   [bp-clients.tv-app.common.events :as common-events]
   [bp-clients.tv-app.common.subs :as common-subs]
   [bp-clients.tv-app.nav.adapters.menu :as menu-adapter]
   [bp-clients.tv-app.nav.events :as nav-events]
   [bp-clients.tv-app.nav.subs :as nav-subs]
   [bp-clients.tv-app.nav.views.loading :refer [loading]]
   [bp-clients.tv-app.router :as router]
   [bp-clients.tv-app.utils.keycodes :as keycodes]
   [bp-clients.tv-app.utils.logger :as logger]
   [bp-clients.tv-app.utils.os :as os-system]
   [bp-clients.tv-app.utils.tree-walker :as tree-walker]
   [bp-clients.tv-app.utils.utils :refer [cloudinary-image] :as utils]
   [bp-libs.design-system.components.menu :refer [tv]]
   [re-frame.core :as rf]
   [shaka-player])
  (:import [goog.events EventType]))

(defn- get-tab-index
  [node]
  (or (some->> node
               (tree-walker/get-attribute "tabindex")
               tree-walker/parseInt) 0))

(defn- tab-index-is-x?
  [x node]
  (= (get-tab-index node) x))

(def ^:private has-tab-index?
  (partial tree-walker/has-attribute? "tabindex"))

(def ^:private tab-index-equal-20
  (partial tab-index-is-x? 20))

(def ^:private tab-filter-vertical
  (every-pred tree-walker/node? has-tab-index? tab-index-equal-20))

(def ^:privete move-up-raw
  (partial tree-walker/move-raw (partial tree-walker/node-filter tab-filter-vertical) tree-walker/previous-node))

(def ^:private move-down-raw
  (partial tree-walker/move-raw (partial tree-walker/node-filter tab-filter-vertical) tree-walker/next-node))

(defn move
  [move-fn]
  (let [active (tree-walker/active-element-or-document)
        el     (move-fn active)]
    (when el (.focus el))))

(def ^:private move-up
  (partial move move-up-raw))

(def ^:private move-down
  (partial move move-down-raw))

(defn move-right
  "This function navigates to right using SpatialNavigation external script.
  It is used to navigate from menu to pages content, to fit the best oblique match,
  Since menu items are not necessarily aligned with page content components, this
  macth can be more complicated.

  This is why there is no move-left here. Because from menu, the user can only navigate
  to right."
  []
  (rf/dispatch [::nav-events/set-menu false])
  (.focus js/SpatialNavigation "content"))

(defn- on-nav-item-click
  [clicked-nav]
  (let [active-nav @(rf/subscribe [::nav-subs/active-nav])
        active-page @(rf/subscribe [::nav-subs/active-page])]
    (rf/dispatch [::nav-events/set-menu false])
    (cond
      (and (= active-page :page-list) (= active-nav clicked-nav))
      (move-right)

      (= clicked-nav :search)
      (rf/dispatch [::nav-events/navigate ::router/search])

      (= clicked-nav :logout)
      (rf/dispatch [::nav-events/navigate ::router/logout])

      :else
      (rf/dispatch [::nav-events/navigate ::router/page {:path-params {:page-id clicked-nav}}]))))

(defn- close-player
  [playlist-slug]
  (when playlist-slug
    (rf/dispatch [::nav-events/fetch-playlist playlist-slug]))
  (rf/dispatch [::common-events/close-player]))

(defn- player-ready
  [handler]
  (rf/dispatch [::common-events/player-ready handler]))

(defn- play-pause
  [video-el playing?]
  (if playing?
    (.pause video-el)
    (.play video-el)))

(defn- on-player-error [event]
  ;; TODO explore this scenario
  ;; how we know it is a fatal error?
  ;; show an error page?
  (logger/js-error event))

(defn- on-ended []
  (let [media-sub             (rf/subscribe [::common-subs/current-media])
        next-media-for-player (get-in @media-sub [:drm :next_media_for_player])
        current-slug          (get-in @media-sub [:playlist :slug])]
    (if next-media-for-player
      (rf/dispatch [::common-events/open-player
                    {:id (:id next-media-for-player)}
                    {:slug (get-in next-media-for-player [:playlist :slug])}])
      (close-player current-slug))))

(defn- player-update
  [event]
  (let [video-el (-> event .-target)
        type     (.-type event)
        paused?  (.-paused video-el)]
    (condp = type
      EventType/ERROR   (on-player-error event)
      EventType/SEEKED  (play-pause video-el false)
      "PLAY/PAUSE"      (play-pause video-el (not paused?))
      EventType/ENDED   (on-ended)
      true)
    (rf/dispatch [::common-events/player-update {:type         type
                                                 :current-time (.-currentTime video-el)
                                                 :paused?      paused?}])))
(defn authenticated
  "Main app's navigation when user is logged-in"
  [page]
  (fn []
    (let [menu-element-ref (createRef)
          menu-open?       @(rf/subscribe [::nav-subs/menu-open?])
          menu-items       @(rf/subscribe [::nav-subs/menu-items])
          active-menu      @(rf/subscribe [::nav-subs/active-nav])
          menu-items       (menu-adapter/menu-raw->menu-db-data menu-items)
          menu-items       (if (get menu-items active-menu)
                             (assoc-in menu-items [active-menu :selected?] true)
                             menu-items)
          menu-items       (-> menu-items
                               vals
                               (as-> new-nav (sort-by :index new-nav)))
          user-profile     @(rf/subscribe [::auth-subs/user-profile])
          playlist-slug    @(rf/subscribe [::nav-subs/active-playlist])
          avatar           (-> user-profile :avatar)
          avatar-image-url (:image avatar)
          playing?         @(rf/subscribe [::common-subs/playing?])
          player-loading?  @(rf/subscribe [::common-subs/player-loading?])
          avatar?          (and (-> avatar :version seq) (-> avatar :image seq))]

      ;; Redirects QA user to staging
      (os-system/qa-staging-redirect user-profile)

      (if playing?
        [:div {:class "w-screen h-screen flex items-center justify-center bg-black relative"}
         (when player-loading? [:div {:class "absolute top-0 w-full h-full z-10 opacity-50"} [loading]])
         [player {:class      "w-screen h-screen player-container"
                  :tab-index  10
                  :events     {:on-close-player          (partial close-player playlist-slug)
                               :on-player-ready          player-ready
                               :on-video-update          player-update
                               :on-pause                 player-update
                               :set-media-info           #(rf/dispatch [::common-events/set-media-info %])
                               :toggle-track-selector    #(rf/dispatch [::common-events/toggle-track-selector])
                               :select-audio             #(rf/dispatch [::common-events/select-audio-track %])
                               :select-subtitle          #(rf/dispatch [::common-events/select-subtitle-track %])
                               :on-show-player-controls  #(rf/dispatch [::common-events/show-player-controls %])
                               :on-next-video            #(rf/dispatch [::common-events/open-player %1 %2])
                               :on-skip-forward          (partial js/console.log "skip-forward")
                               :on-skip-backwards        (partial js/console.log "skip-backward")
                               :on-change-audio-track    (partial js/console.log "audio-track")
                               :on-change-subtitle-track (partial js/console.log "subtitle-track")}
                  :config-sub [::common-subs/player]
                  :token-sub  [::auth-subs/token]
                  :media-sub  [::common-subs/current-media]
                  :user-sub   [::auth-subs/user-profile]}]]
        [:div {:class "w-full relative overflow-hidden"}
         [tv {:items       menu-items
              :name        (:name user-profile)
              :user-avatar (cloudinary-image (if avatar? avatar-image-url "/profile/logobp"))
              :tab-index   20
              :on-key-down #(let [event   %
                                  keyCode (-> event .-keyCode)]
                              (cond
                                (keycodes/left? keyCode)
                                (utils/stop-event-propagation event)

                                (keycodes/ok? keyCode)
                                (do
                                  (utils/stop-event-propagation event)
                                  (.click js/document.activeElement))

                                (keycodes/right? keyCode)
                                (do
                                  (utils/stop-event-propagation event)
                                  (move-right))

                                (keycodes/up? keyCode)
                                (do
                                  (utils/stop-event-propagation event)
                                  (move-up))

                                (keycodes/down? keyCode)
                                (do
                                  (utils/stop-event-propagation event)
                                  (move-down))

                                :else nil))
              :class         "min-h-screen absolute z-30 peer"
              :on-item-click on-nav-item-click
              :on-focus      (fn [_]
                               (rf/dispatch [::nav-events/set-menu true])
                               (when-not menu-open?
                                 (let [menu-pages-items  (-> menu-element-ref
                                                             .-current
                                                             .-children
                                                             (.item 1))
                                       current-page-idx  (.indexOf (map :page_id menu-items)
                                                                   (clj->js active-menu))
                                       current-page-item (-> menu-pages-items
                                                             .-children
                                                             (.item current-page-idx))]
                                   (when current-page-item
                                     (.focus current-page-item)))))
              :closed?       true
              :react-ref     menu-element-ref}]
         [:div {:class ["ml-[5vw] -z-20"
                        "peer-focus-within:image-gradient-left peer-hover:image-gradient-left"]} page]]))))
